import { ref, computed, onUnmounted, type Ref } from 'vue';

export interface UseSplitViewOptions {
  /** Initial ratio (0-1) for the left panel. Defaults to 0.5 */
  defaultRatio?: number;
  /** Minimum ratio (0-1) for the left panel. Defaults to 0.25 */
  minRatio?: number;
  /** Maximum ratio (0-1) for the left panel. Defaults to 0.75 */
  maxRatio?: number;
  /** localStorage key for persistence. When omitted, the value is not persisted. */
  storageKey?: string;
}

/**
 * Generic split-view state with a draggable divider.
 *
 * Returns:
 * - `leftRatio` (0..1) — reactive current ratio
 * - `isDragging` — true while the user is dragging
 * - `startDrag(event, container)` — call from mousedown/touchstart on the divider
 * - `reset()` — reset to defaultRatio
 *
 * Each drag session is self-contained: `startDrag` creates fresh move/end
 * handlers that capture the container element in their closure. Window
 * listeners are attached on drag start and removed on drag end (or on unmount).
 *
 * @example
 * const { leftRatio, startDrag } = useSplitView({
 *   defaultRatio: 0.5,
 *   minRatio: 0.25,
 *   maxRatio: 0.75,
 *   storageKey: 'logs-split-ratio',
 * });
 */
export const useSplitView = (options: UseSplitViewOptions = {}) => {
  const defaultRatio = options.defaultRatio ?? 0.5;
  const minRatio = options.minRatio ?? 0.25;
  const maxRatio = options.maxRatio ?? 0.75;
  const storageKey = options.storageKey;

  const clamp = (value: number): number => Math.max(minRatio, Math.min(maxRatio, value));

  // Read initial value from localStorage if available, else default
  const readStored = (): number => {
    if (!storageKey || typeof window === 'undefined') {
      return defaultRatio;
    }
    try {
      const raw = window.localStorage.getItem(storageKey);
      if (raw === null) {
        return defaultRatio;
      }
      const parsed = parseFloat(raw);
      if (isNaN(parsed)) {
        return defaultRatio;
      }
      return clamp(parsed);
    } catch {
      return defaultRatio;
    }
  };

  const leftRatio: Ref<number> = ref(readStored());
  const isDragging = ref(false);

  const persist = (value: number) => {
    if (!storageKey || typeof window === 'undefined') {
      return;
    }
    try {
      window.localStorage.setItem(storageKey, value.toString());
    } catch {
      /* noop — storage may be unavailable (privacy mode, quota, etc.) */
    }
  };

  const setRatio = (value: number) => {
    const clamped = clamp(value);
    leftRatio.value = clamped;
    persist(clamped);
  };

  const getEventClientX = (event: MouseEvent | TouchEvent): number => {
    if ('touches' in event && event.touches.length > 0) {
      return event.touches[0].clientX;
    }
    if ('changedTouches' in event && event.changedTouches.length > 0) {
      return event.changedTouches[0].clientX;
    }
    return (event as MouseEvent).clientX;
  };

  // Cleanup function for the current drag session. Captures the move/end
  // handlers created in startDrag so they can be removed on drag end or
  // component unmount.
  let currentDragCleanup: (() => void) | null = null;

  /**
   * Start dragging. Call from mousedown/touchstart on the divider.
   * `container` is the element whose width defines 0%–100% of the ratio.
   *
   * The container reference lives in the closure of this call only — there's
   * no shared `containerEl` state on the composable, so each drag session
   * is fully isolated.
   */
  const startDrag = (event: MouseEvent | TouchEvent, container: HTMLElement | null) => {
    if (!container) {
      return;
    }
    event.preventDefault();

    // If a previous drag somehow left listeners attached (shouldn't happen),
    // clean them up before starting a new session.
    if (currentDragCleanup) {
      currentDragCleanup();
    }

    isDragging.value = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    const onDragMove = (moveEvent: MouseEvent | TouchEvent) => {
      moveEvent.preventDefault();
      const rect = container.getBoundingClientRect();
      if (rect.width === 0) {
        return;
      }
      const clientX = getEventClientX(moveEvent);
      const ratio = (clientX - rect.left) / rect.width;
      setRatio(ratio);
    };

    const onDragEnd = () => {
      isDragging.value = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      cleanup();
    };

    const cleanup = () => {
      window.removeEventListener('mousemove', onDragMove);
      window.removeEventListener('mouseup', onDragEnd);
      window.removeEventListener('touchmove', onDragMove);
      window.removeEventListener('touchend', onDragEnd);
      window.removeEventListener('touchcancel', onDragEnd);
      currentDragCleanup = null;
    };

    window.addEventListener('mousemove', onDragMove);
    window.addEventListener('mouseup', onDragEnd);
    window.addEventListener('touchmove', onDragMove, { passive: false });
    window.addEventListener('touchend', onDragEnd);
    window.addEventListener('touchcancel', onDragEnd);

    currentDragCleanup = cleanup;
  };

  const reset = () => {
    setRatio(defaultRatio);
  };

  const leftPercent = computed(() => `${(leftRatio.value * 100).toFixed(2)}%`);
  const rightPercent = computed(() => `${((1 - leftRatio.value) * 100).toFixed(2)}%`);

  onUnmounted(() => {
    // If the component is unmounted mid-drag, release any lingering listeners
    // so we don't leak them to the window.
    if (currentDragCleanup) {
      currentDragCleanup();
    }
  });

  return {
    leftRatio,
    leftPercent,
    rightPercent,
    isDragging,
    startDrag,
    reset,
    setRatio,
  };
};
